import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  useScrollTrigger,
} from "@mui/material";
import {
  ShoppingCart,
  Boxes,
  Wallet,
  Truck,
  TrendingUp,
  FileCheck2,
  ArrowRight,
  Check,
  ChevronDown,
  Sparkles,
  ShieldCheck,
  Zap,
  Headphones,
  Layers,
  BarChart3,
  Menu as MenuIcon,
} from "lucide-react";
import Logo from "../components/Logo";
import { brand } from "../theme";

/* -------------------------------------------------------------- */
/* Reusable bits                                                  */
/* -------------------------------------------------------------- */
const Section = ({ children, dark = false, sx = {}, id }) => (
  <Box
    component="section"
    id={id}
    sx={{
      py: { xs: 8, md: 12 },
      bgcolor: dark ? brand.navy : "transparent",
      color: dark ? "#fff" : "inherit",
      position: "relative",
      ...sx,
    }}
  >
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

const SectionEyebrow = ({ children, dark = false }) => (
  <Typography
    variant="overline"
    sx={{
      color: dark ? "#7dd3fc" : brand.primary,
      fontWeight: 700,
      letterSpacing: "2.4px",
      fontSize: "0.72rem",
    }}
  >
    {children}
  </Typography>
);

const SectionHeading = ({ eyebrow, title, subtitle, dark = false, align = "center" }) => (
  <Stack spacing={2} alignItems={align === "center" ? "center" : "flex-start"} sx={{ mb: 7, textAlign: align }}>
    {eyebrow && <SectionEyebrow dark={dark}>{eyebrow}</SectionEyebrow>}
    <Typography
      variant="h3"
      sx={{
        fontWeight: 800,
        color: dark ? "#fff" : brand.navy,
        maxWidth: 760,
        letterSpacing: "-0.02em",
        lineHeight: 1.15,
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="body1"
        sx={{
          color: dark ? "rgba(255,255,255,0.72)" : "text.secondary",
          maxWidth: 680,
          fontSize: "1.05rem",
          lineHeight: 1.65,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Stack>
);

/* -------------------------------------------------------------- */
/* TopBar                                                         */
/* -------------------------------------------------------------- */
function TopBar() {
  const elevated = useScrollTrigger({ disableHysteresis: true, threshold: 12 });
  const [open, setOpen] = useState(false);

  const links = [
    { id: "modulos", label: "Recursos" },
    { id: "como-funciona", label: "Como funciona" },
    { id: "integracoes", label: "Integrações" },
    { id: "faq", label: "FAQ" },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <Box
      component="header"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backdropFilter: "blur(14px)",
        backgroundColor: elevated ? "rgba(15,23,42,0.85)" : "rgba(15,23,42,0.55)",
        borderBottom: elevated ? "1px solid rgba(255,255,255,0.08)" : "1px solid transparent",
        transition: "all 0.25s ease",
      }}
      data-testid="landing-topbar"
    >
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: 70 }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{ textDecoration: "none", display: "flex", alignItems: "center" }}
            data-testid="landing-logo-link"
          >
            <Logo variant="stacked" size={32} color="#fff" />
          </Box>

          <Stack
            direction="row"
            spacing={4}
            alignItems="center"
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {links.map((l) => (
              <Typography
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                sx={{
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.78)",
                  fontWeight: 500,
                  fontSize: "0.92rem",
                  transition: "color 0.2s",
                  "&:hover": { color: "#fff" },
                }}
              >
                {l.label}
              </Typography>
            ))}
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              component={RouterLink}
              to="/login"
              variant="text"
              data-testid="nav-login-btn"
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontWeight: 600,
                "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              Entrar
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              endIcon={<ArrowRight size={16} />}
              data-testid="nav-start-btn"
              sx={{
                bgcolor: brand.primary,
                color: "#fff",
                px: 2.5,
                py: 1,
                "&:hover": { bgcolor: brand.primaryDark },
                boxShadow: "0 8px 28px -8px rgba(14,165,233,0.55)",
              }}
            >
              Começar agora
            </Button>
            <IconButton
              sx={{ display: { xs: "inline-flex", md: "none" }, color: "#fff" }}
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-btn"
            >
              <MenuIcon size={20} />
            </IconButton>
          </Stack>
        </Stack>

        {open && (
          <Stack
            spacing={1.5}
            sx={{
              display: { xs: "flex", md: "none" },
              pb: 3,
              pt: 1,
              borderTop: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {links.map((l) => (
              <Typography
                key={l.id}
                onClick={() => scrollTo(l.id)}
                sx={{ cursor: "pointer", color: "rgba(255,255,255,0.85)", py: 1 }}
              >
                {l.label}
              </Typography>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}

/* -------------------------------------------------------------- */
/* Hero                                                           */
/* -------------------------------------------------------------- */
function Hero() {
  return (
    <Box
      sx={{
        position: "relative",
        pt: { xs: 14, md: 18 },
        pb: { xs: 8, md: 14 },
        overflow: "hidden",
        backgroundColor: brand.navy,
        backgroundImage: `
          radial-gradient(900px 480px at 12% 0%, rgba(14,165,233,0.28), transparent 60%),
          radial-gradient(900px 520px at 90% 100%, rgba(56,189,248,0.18), transparent 60%),
          linear-gradient(180deg, #0b1426 0%, #0f172a 60%, #0b1426 100%)
        `,
      }}
      data-testid="landing-hero"
    >
      {/* Subtle grid overlay */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.18,
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 80%)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={3.5} sx={{ animation: "fadeUp 0.7s ease both" }}>
              <Chip
                icon={<Sparkles size={14} />}
                label="Sistema completo • Multi-empresa • Multi-loja"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "rgba(14,165,233,0.12)",
                  color: "#7dd3fc",
                  fontWeight: 600,
                  border: "1px solid rgba(14,165,233,0.3)",
                  "& .MuiChip-icon": { color: "#7dd3fc" },
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  color: "#fff",
                  fontSize: { xs: "2.4rem", sm: "3rem", md: "3.6rem", lg: "4rem" },
                  fontWeight: 800,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                }}
                data-testid="hero-headline"
              >
                Sua empresa
                <br />
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(120deg, ${brand.primary} 0%, #7dd3fc 50%, #38bdf8 100%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  controlada por você.
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: "rgba(226,232,240,0.78)",
                  fontSize: { xs: "1rem", md: "1.18rem" },
                  lineHeight: 1.65,
                  maxWidth: 580,
                }}
                data-testid="hero-subhead"
              >
                PDV, estoque, financeiro, compras, vendas e fiscal em um único sistema.
                Integrado com WhatsApp, PIX e Nota Fiscal eletrônica para você parar de
                apagar incêndios e começar a fazer sua empresa crescer.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 1 }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  size="large"
                  variant="contained"
                  endIcon={<ArrowRight size={18} />}
                  data-testid="hero-cta-primary"
                  sx={{
                    bgcolor: brand.primary,
                    color: "#fff",
                    px: 4,
                    py: 1.6,
                    fontSize: "1rem",
                    fontWeight: 700,
                    "&:hover": { bgcolor: brand.primaryDark, transform: "translateY(-1px)" },
                    transition: "all 0.2s",
                    boxShadow: "0 12px 36px -8px rgba(14,165,233,0.55)",
                  }}
                >
                  Começar agora
                </Button>
                <Button
                  href="#modulos"
                  size="large"
                  variant="outlined"
                  data-testid="hero-cta-secondary"
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.25)",
                    px: 4,
                    py: 1.6,
                    fontSize: "1rem",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,0.06)",
                    },
                  }}
                >
                  Ver recursos
                </Button>
              </Stack>

              <Stack direction="row" spacing={3} sx={{ pt: 2, flexWrap: "wrap" }}>
                {[
                  { icon: <Check size={16} />, t: "Sem instalação" },
                  { icon: <Check size={16} />, t: "Suporte humano" },
                  { icon: <Check size={16} />, t: "Atualizações contínuas" },
                ].map((it) => (
                  <Stack
                    key={it.t}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ color: "rgba(226,232,240,0.7)", fontSize: "0.9rem" }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        bgcolor: "rgba(16,185,129,0.18)",
                        color: "#34d399",
                        borderRadius: "50%",
                        p: 0.4,
                      }}
                    >
                      {it.icon}
                    </Box>
                    <Typography variant="body2">{it.t}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <HeroVisual />
          </Grid>
        </Grid>

        {/* Brand keywords strip */}
        <Box
          sx={{
            mt: { xs: 8, md: 12 },
            pt: 4,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: { xs: 2, md: 5 },
          }}
        >
          {["CONTROLE", "ORGANIZAÇÃO", "CRESCIMENTO", "INTEGRAÇÃO"].map((k, i) => (
            <Stack key={k} direction="row" spacing={2} alignItems="center">
              <Typography
                sx={{
                  letterSpacing: "4px",
                  fontWeight: 700,
                  color: "rgba(226,232,240,0.55)",
                  fontSize: "0.8rem",
                }}
              >
                {k}
              </Typography>
              {i < 3 && (
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: brand.primary,
                    display: { xs: "none", md: "block" },
                  }}
                />
              )}
            </Stack>
          ))}
        </Box>
      </Container>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
}

function HeroVisual() {
  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: 340, sm: 380, md: 460 },
        animation: "fadeUp 0.9s ease 0.15s both",
      }}
      data-testid="hero-visual"
    >
      {/* Card 1 - Big metric */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: { xs: 0, md: 0 },
          width: { xs: "82%", md: "85%" },
          p: 2.5,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(18px)",
          boxShadow: "0 24px 60px -16px rgba(2,6,23,0.7)",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.5}>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.78rem" }}>
              Faturamento de hoje
            </Typography>
            <Typography
              sx={{ color: "#fff", fontWeight: 800, fontSize: "1.75rem", lineHeight: 1 }}
            >
              R$ 12.847,30
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
              <TrendingUp size={14} color="#34d399" />
              <Typography sx={{ color: "#34d399", fontSize: "0.78rem", fontWeight: 600 }}>
                +18,4% vs ontem
              </Typography>
            </Stack>
          </Stack>
          <Box
            sx={{
              p: 1.2,
              borderRadius: 2,
              bgcolor: "rgba(14,165,233,0.18)",
              color: brand.primary,
            }}
          >
            <BarChart3 size={20} />
          </Box>
        </Stack>
        {/* Mini bars */}
        <Stack direction="row" spacing={0.5} sx={{ mt: 2, alignItems: "flex-end", height: 40 }}>
          {[35, 50, 28, 65, 48, 80, 72].map((h, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: `${h}%`,
                borderRadius: "3px 3px 0 0",
                background:
                  i === 5
                    ? `linear-gradient(180deg, ${brand.primary}, #38bdf8)`
                    : "rgba(148,163,184,0.35)",
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Card 2 - Stock alert */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 195, md: 220 },
          left: { xs: 0, md: -10 },
          width: { xs: "78%", md: "70%" },
          p: 2,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.14)",
          backdropFilter: "blur(18px)",
          boxShadow: "0 16px 40px -12px rgba(2,6,23,0.7)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: "rgba(245,158,11,0.18)",
              color: "#fbbf24",
            }}
          >
            <Boxes size={18} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.86rem" }}>
              Estoque baixo: 4 itens
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.74rem" }}>
              Reposição automática sugerida
            </Typography>
          </Box>
          <Chip
            label="Ver"
            size="small"
            sx={{
              bgcolor: "rgba(14,165,233,0.18)",
              color: "#7dd3fc",
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          />
        </Stack>
      </Box>

      {/* Card 3 - PIX received */}
      <Box
        sx={{
          position: "absolute",
          bottom: { xs: 0, md: 20 },
          right: { xs: 0, md: 30 },
          width: { xs: "70%", md: "60%" },
          p: 2,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.14)",
          backdropFilter: "blur(18px)",
          boxShadow: "0 16px 40px -12px rgba(2,6,23,0.7)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: "rgba(16,185,129,0.2)",
              color: "#34d399",
            }}
          >
            <Wallet size={18} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.86rem" }}>
              PIX recebido • R$ 248,90
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.74rem" }}>
              Mercado Pago • há 2 minutos
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------- */
/* Problemas que resolvemos                                       */
/* -------------------------------------------------------------- */
const problems = [
  {
    title: "Vendas perdidas por falta de controle",
    body:
      "Cliente quer comprar e você não sabe se tem em estoque. Itens no PDV com preços errados. Promessas de entrega que furam.",
  },
  {
    title: "Caixa apertado sem saber por quê",
    body:
      "Contas a pagar bagunçadas, recebíveis sem cobrança, parcelas que atrasam e ninguém percebe. O dinheiro entra e some.",
  },
  {
    title: "Burocracia fiscal que consome o seu tempo",
    body:
      "NF-e, certificado, XML de fornecedor, conciliação… Cada coisa em um sistema diferente. Erros caros e horas desperdiçadas.",
  },
];

function Problems() {
  return (
    <Section sx={{ bgcolor: "#f8fafc" }}>
      <SectionHeading
        eyebrow="O QUE TIRA O SONO DE QUEM EMPREENDE"
        title="Sua empresa não pode parar todo dia para apagar incêndio."
        subtitle="O Stock Sistema substitui planilhas soltas, sistemas desconectados e processos manuais por um único lugar onde tudo conversa."
      />
      <Grid container spacing={3}>
        {problems.map((p, i) => (
          <Grid size={{ xs: 12, md: 4 }} key={p.title}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${brand.surface}`,
                bgcolor: "#fff",
                transition: "all 0.25s",
                "&:hover": {
                  borderColor: brand.primary,
                  transform: "translateY(-4px)",
                  boxShadow: "0 18px 40px -16px rgba(14,165,233,0.25)",
                },
              }}
              data-testid={`problem-card-${i}`}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(239,68,68,0.1)",
                    color: "#ef4444",
                    mb: 2,
                    fontWeight: 800,
                    fontSize: "1.05rem",
                  }}
                >
                  0{i + 1}
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: brand.navy, mb: 1.5, fontSize: "1.1rem" }}
                >
                  {p.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.65, fontSize: "0.94rem" }}
                >
                  {p.body}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

/* -------------------------------------------------------------- */
/* Módulos                                                        */
/* -------------------------------------------------------------- */
const modules = [
  {
    icon: <ShoppingCart size={22} />,
    title: "PDV inteligente",
    body:
      "Frente de caixa rápida, leitor de código, múltiplas formas de pagamento, PIX integrado, recibo, NF-e e avaliação por WhatsApp.",
    bullets: ["Cupom fiscal", "PIX e cartão", "Recibo digital"],
  },
  {
    icon: <Boxes size={22} />,
    title: "Estoque sob controle",
    body:
      "Movimentações automáticas, alerta de estoque baixo, importação de XML de fornecedor, multi-loja e rastreamento por lote.",
    bullets: ["Alertas em tempo real", "XML NF-e", "Multi-loja"],
  },
  {
    icon: <Wallet size={22} />,
    title: "Financeiro completo",
    body:
      "Contas a pagar e a receber, parcelas automáticas, cobrança recorrente, conciliação bancária e fluxo de caixa visual.",
    bullets: ["Parcelas automáticas", "Cobrança recorrente", "Fluxo de caixa"],
  },
  {
    icon: <Truck size={22} />,
    title: "Compras com fornecedor",
    body:
      "Pedidos de compra, recebimento com conferência, atualização de custo automática, histórico por fornecedor.",
    bullets: ["Conferência guiada", "Custo médio", "Histórico"],
  },
  {
    icon: <TrendingUp size={22} />,
    title: "Vendas e orçamentos",
    body:
      "Orçamentos, aprovação, conversão em venda com promissória, integração com clientes e cobrança recorrente.",
    bullets: ["Orçamento → Venda", "Promissória", "CRM básico"],
  },
  {
    icon: <FileCheck2 size={22} />,
    title: "Fiscal sem estresse",
    body:
      "Emissão de NF-e/NFC-e, MDF-e, certificado digital, integração com Nuvem Fiscal e busca DFe automática.",
    bullets: ["NF-e/NFC-e", "Nuvem Fiscal", "Busca DFe"],
  },
];

function Modules() {
  return (
    <Section id="modulos">
      <SectionHeading
        eyebrow="UM SISTEMA, SEIS MOTORES"
        title="Tudo o que você precisa para operar, em um só lugar."
        subtitle="Cada módulo conversa com os outros. Uma venda no PDV atualiza o estoque, gera o financeiro, emite a nota e dispara avaliação no WhatsApp — automático."
      />
      <Grid container spacing={3}>
        {modules.map((m, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={m.title}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 3,
                border: `1px solid ${brand.surface}`,
                transition: "all 0.25s",
                "&:hover": {
                  transform: "translateY(-6px)",
                  borderColor: brand.primary,
                  boxShadow: "0 24px 50px -20px rgba(14,165,233,0.35)",
                  "& .module-icon": {
                    bgcolor: brand.primary,
                    color: "#fff",
                  },
                },
              }}
              data-testid={`module-card-${i}`}
            >
              <CardContent sx={{ p: 3.5 }}>
                <Box
                  className="module-icon"
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(14,165,233,0.12)",
                    color: brand.primary,
                    mb: 2.5,
                    transition: "all 0.25s",
                  }}
                >
                  {m.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: brand.navy, mb: 1.2, fontSize: "1.12rem" }}
                >
                  {m.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", lineHeight: 1.6, mb: 2, fontSize: "0.92rem" }}
                >
                  {m.body}
                </Typography>
                <Stack spacing={0.8}>
                  {m.bullets.map((b) => (
                    <Stack key={b} direction="row" spacing={1} alignItems="center">
                      <Check size={14} color={brand.primary} />
                      <Typography sx={{ fontSize: "0.84rem", color: brand.slate }}>
                        {b}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Section>
  );
}

/* -------------------------------------------------------------- */
/* Como funciona                                                  */
/* -------------------------------------------------------------- */
const steps = [
  {
    n: "01",
    title: "Configure em minutos",
    body:
      "Cadastre sua empresa, importe seus produtos e seus clientes. O sistema cresce junto com você — multi-loja desde o primeiro dia.",
  },
  {
    n: "02",
    title: "Conecte suas integrações",
    body:
      "WhatsApp para avaliações, Mercado Pago para PIX, Nuvem Fiscal para NF-e. Tudo em poucos cliques, sem precisar de TI.",
  },
  {
    n: "03",
    title: "Venda, controle, cresça",
    body:
      "Venda no PDV, acompanhe o caixa em tempo real, receba alertas no WhatsApp e tome decisões com dashboards de verdade.",
  },
];

function HowItWorks() {
  return (
    <Section id="como-funciona" sx={{ bgcolor: "#f8fafc" }}>
      <SectionHeading
        eyebrow="DO ZERO À OPERAÇÃO EM 3 PASSOS"
        title="Simples de começar. Poderoso para escalar."
      />
      <Box sx={{ position: "relative" }}>
        <Grid container spacing={4}>
          {steps.map((s, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={s.n}>
              <Stack spacing={2.5} sx={{ position: "relative" }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryDark} 100%)`,
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: "1.05rem",
                    boxShadow: "0 12px 30px -10px rgba(14,165,233,0.55)",
                  }}
                >
                  {s.n}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: brand.navy }}>
                  {s.title}
                </Typography>
                <Typography sx={{ color: "text.secondary", lineHeight: 1.65 }}>
                  {s.body}
                </Typography>

                {/* Connector line on desktop */}
                {i < steps.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: "none", md: "block" },
                      position: "absolute",
                      top: 28,
                      right: -32,
                      width: 64,
                      height: 1,
                      borderTop: `2px dashed ${brand.surface}`,
                    }}
                  />
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Section>
  );
}

/* -------------------------------------------------------------- */
/* Benefícios / Diferenciais                                      */
/* -------------------------------------------------------------- */
const benefits = [
  {
    icon: <Layers size={22} />,
    title: "Multi-empresa, multi-loja",
    body: "Gerencie quantas empresas e filiais quiser, com dados isolados e relatórios consolidados.",
  },
  {
    icon: <ShieldCheck size={22} />,
    title: "Seus dados protegidos",
    body: "Backup contínuo, criptografia em trânsito e isolamento total entre empresas (multi-tenant).",
  },
  {
    icon: <Zap size={22} />,
    title: "Atualizações contínuas",
    body: "Novas funcionalidades todo mês — sem custo extra, sem atualização manual, sem dor de cabeça.",
  },
  {
    icon: <Headphones size={22} />,
    title: "Suporte que entende seu negócio",
    body: "Atendimento humano em português pelo WhatsApp, com tempo médio de resposta inferior a 30 minutos.",
  },
];

function Benefits() {
  return (
    <Section id="integracoes" dark>
      <SectionHeading
        eyebrow="POR QUE STOCK SISTEMA"
        title="Mais que software. Um copiloto para sua empresa."
        subtitle="Construído com base no que comerciantes brasileiros realmente precisam — sem firulas, com integrações reais e suporte em português."
        dark
      />
      <Grid container spacing={3}>
        {benefits.map((b, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={b.title}>
            <Box
              sx={{
                p: 3.5,
                height: "100%",
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                transition: "all 0.25s",
                "&:hover": {
                  borderColor: "rgba(14,165,233,0.4)",
                  bgcolor: "rgba(14,165,233,0.06)",
                },
              }}
              data-testid={`benefit-card-${i}`}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(14,165,233,0.18)",
                  color: "#7dd3fc",
                  mb: 2,
                }}
              >
                {b.icon}
              </Box>
              <Typography
                variant="h6"
                sx={{ color: "#fff", fontWeight: 700, mb: 1, fontSize: "1.02rem" }}
              >
                {b.title}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: "0.9rem",
                  lineHeight: 1.6,
                }}
              >
                {b.body}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Integrações strip */}
      <Box
        sx={{
          mt: 8,
          pt: 5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: "rgba(255,255,255,0.4)",
            fontSize: "0.78rem",
            letterSpacing: "3px",
            fontWeight: 600,
            mb: 3,
          }}
        >
          INTEGRAÇÕES NATIVAS
        </Typography>
        <Stack
          direction="row"
          spacing={{ xs: 3, md: 6 }}
          justifyContent="center"
          flexWrap="wrap"
          sx={{ gap: { xs: 2, md: 4 } }}
        >
          {["WhatsApp", "Mercado Pago", "Nuvem Fiscal", "PIX", "NF-e / NFC-e", "MDF-e"].map(
            (name) => (
              <Typography
                key={name}
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  px: 1.5,
                  py: 0.8,
                  borderRadius: 2,
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {name}
              </Typography>
            )
          )}
        </Stack>
      </Box>
    </Section>
  );
}

/* -------------------------------------------------------------- */
/* FAQ                                                            */
/* -------------------------------------------------------------- */
const faqs = [
  {
    q: "Como funciona a contratação?",
    a: "Você entra em contato pelo botão 'Começar agora' e nosso time monta uma proposta personalizada para o seu negócio. O cadastro inicial é gratuito e você só paga após validar que o sistema atende sua operação.",
  },
  {
    q: "Preciso instalar alguma coisa?",
    a: "Não. O Stock Sistema funciona 100% no navegador, em qualquer computador, tablet ou celular. Basta ter internet — funciona até no 4G da loja.",
  },
  {
    q: "Funciona para mais de uma loja ou empresa?",
    a: "Sim. O sistema é multi-empresa e multi-loja desde o primeiro dia. Cada empresa tem seus dados isolados, e supervisores podem consolidar relatórios.",
  },
  {
    q: "Vocês emitem NF-e e NFC-e?",
    a: "Sim. Temos integração nativa com a Nuvem Fiscal para emissão de NF-e, NFC-e e MDF-e. Você só precisa do certificado digital A1 da sua empresa.",
  },
  {
    q: "Meus dados ficam seguros?",
    a: "Sim. Trabalhamos com criptografia em trânsito (HTTPS), isolamento multi-tenant (cada empresa em sua própria estrutura lógica) e backup contínuo da base de dados.",
  },
  {
    q: "Tem treinamento e suporte?",
    a: "Tem. Onboarding guiado, vídeos curtos e suporte humano em português via WhatsApp com tempo médio de resposta de 30 minutos.",
  },
];

function FAQ() {
  return (
    <Section id="faq">
      <SectionHeading
        eyebrow="DÚVIDAS FREQUENTES"
        title="Tudo o que você queria saber antes de assinar."
      />
      <Box sx={{ maxWidth: 820, mx: "auto" }}>
        {faqs.map((f, i) => (
          <Accordion
            key={i}
            elevation={0}
            disableGutters
            sx={{
              bgcolor: "transparent",
              borderBottom: `1px solid ${brand.surface}`,
              "&:before": { display: "none" },
              "&.Mui-expanded": { margin: 0 },
            }}
            data-testid={`faq-item-${i}`}
          >
            <AccordionSummary
              expandIcon={<ChevronDown color={brand.primary} />}
              sx={{ py: 2, px: 0, "& .MuiAccordionSummary-content": { my: 0 } }}
            >
              <Typography sx={{ fontWeight: 600, color: brand.navy, fontSize: "1.04rem" }}>
                {f.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 3, px: 0 }}>
              <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>{f.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Section>
  );
}

/* -------------------------------------------------------------- */
/* CTA Final                                                      */
/* -------------------------------------------------------------- */
function FinalCTA() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
        backgroundColor: brand.navy,
        backgroundImage: `
          radial-gradient(700px 380px at 80% 0%, rgba(14,165,233,0.32), transparent 60%),
          radial-gradient(700px 380px at 10% 100%, rgba(56,189,248,0.18), transparent 60%),
          linear-gradient(180deg, #0b1426 0%, #0f172a 100%)
        `,
      }}
      data-testid="final-cta-section"
    >
      <Container maxWidth="md">
        <Stack spacing={3.5} alignItems="center" textAlign="center">
          <Chip
            label="Seu próximo passo"
            sx={{
              bgcolor: "rgba(14,165,233,0.15)",
              color: "#7dd3fc",
              fontWeight: 600,
              border: "1px solid rgba(14,165,233,0.3)",
            }}
          />
          <Typography
            variant="h2"
            sx={{
              color: "#fff",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              fontSize: { xs: "2rem", md: "2.8rem" },
              maxWidth: 720,
              lineHeight: 1.15,
            }}
          >
            Pronto para parar de improvisar e começar a crescer?
          </Typography>
          <Typography
            sx={{
              color: "rgba(226,232,240,0.7)",
              maxWidth: 580,
              fontSize: "1.05rem",
              lineHeight: 1.65,
            }}
          >
            Fale com nosso time, conheça o sistema funcionando com seus dados e descubra
            quanto a sua empresa pode crescer com controle real.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 2 }}>
            <Button
              component={RouterLink}
              to="/login"
              size="large"
              variant="contained"
              endIcon={<ArrowRight size={18} />}
              data-testid="final-cta-primary"
              sx={{
                bgcolor: brand.primary,
                color: "#fff",
                px: 4.5,
                py: 1.7,
                fontSize: "1rem",
                fontWeight: 700,
                "&:hover": { bgcolor: brand.primaryDark, transform: "translateY(-1px)" },
                transition: "all 0.2s",
                boxShadow: "0 14px 36px -8px rgba(14,165,233,0.55)",
              }}
            >
              Começar agora
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              size="large"
              variant="outlined"
              data-testid="final-cta-secondary"
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.3)",
                px: 4.5,
                py: 1.7,
                fontSize: "1rem",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.06)",
                },
              }}
            >
              Entrar no sistema
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

/* -------------------------------------------------------------- */
/* Footer                                                         */
/* -------------------------------------------------------------- */
function Footer() {
  return (
    <Box
      component="footer"
      sx={{ bgcolor: "#0b1426", py: 4, borderTop: "1px solid rgba(255,255,255,0.06)" }}
      data-testid="landing-footer"
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Logo variant="stacked" size={26} color="#fff" />
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>
            © 2026 Stock Sistema. Sua empresa controlada por você.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

/* -------------------------------------------------------------- */
/* Page                                                           */
/* -------------------------------------------------------------- */
export default function Landing() {
  useEffect(() => {
    document.title = "Stock Sistema — Controle, Organização, Crescimento, Integração";
  }, []);

  return (
    <Box sx={{ bgcolor: "#fff" }} data-testid="landing-page">
      <TopBar />
      <Hero />
      <Problems />
      <Modules />
      <HowItWorks />
      <Benefits />
      <FAQ />
      <FinalCTA />
      <Footer />
    </Box>
  );
}
